import { createElement, ReactElement } from "react";
import { toast, ToastOptions } from "react-hot-toast";

import { ApiError } from "@config/apiClient";
import { ERROR_STRINGS } from "@config/messages";

export type ErrorResponse = {
  message?: string;
  detail?: string;
  __all__?: string[];
};

// DRF rejects a payload with per-field messages, nested as deep as the serializer — e.g.
// `{ attribute_values: [{ value: ["Revenue is required"] }] }`. Without this they all read the
// generic fallback.
function collectFieldMessages(value: unknown): string[] {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(collectFieldMessages);
  if (value && typeof value === "object") return Object.values(value).flatMap(collectFieldMessages);
  return [];
}

class ToastService {
  static showError(
    error: ApiError | string,
    userMessage?: ReactElement,
    options?: ToastOptions
  ): void {
    if (userMessage) {
      this.showUserError(createElement("span", null, userMessage), { id: "error", ...options });
      return;
    }

    const errorMessage = this.getErrorMessage(error);
    if (Array.isArray(errorMessage)) {
      errorMessage.forEach((message) => {
        toast.error(message, { id: message, ...options });
      });
    } else {
      toast.error(errorMessage, { id: errorMessage, ...options });
    }
  }

  static showUserError(message: ReactElement, options?: ToastOptions): void {
    toast.error(() => message, { id: "error", ...options });
  }

  static showSuccess(message: string, options?: ToastOptions): void {
    toast.success(message, { id: message, ...options });
  }

  static dismiss(id?: string): void {
    toast.dismiss(id);
  }

  static getErrorMessage(error: ApiError | string): string | string[] {
    if (typeof error === "string") {
      return error;
    } else if (error.response && error.response.data) {
      const responseData = error.response.data as ErrorResponse;
      if (responseData.message) return responseData.message;
      if (responseData.detail) return responseData.detail;
      if (responseData.__all__) return responseData.__all__;

      const fieldMessages = collectFieldMessages(responseData);
      return fieldMessages.length > 0 ? fieldMessages : ERROR_STRINGS.SERVER_ERROR;
    } else if (error.request) {
      return ERROR_STRINGS.SERVER_UNREACHABLE;
    } else {
      return error.message || ERROR_STRINGS.SERVER_ERROR;
    }
  }
}

export default ToastService;
